import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import application from '../services/ApplicationService';
import {
  app_slice_name,
  app_thunks,
  app_sections,
  app_section_ids,
  application_cover_products,
} from '../constants/application';
import {
  getApplicantByApplicantId,
  getApplicantIndexFromMeta,
  getApplicantIndexFromPayload,
  getApplicantIndexFromPayloadData,
  isApplicantStatisfied,
} from './ApplicationSelectors';
import {
  getNavigationSections,
  isSectionDisabled,
} from '../utilities/application/navHelper';
import {
  sortApplicantCovers,
  setEmptyStringPropertiesToNull,
  applyCoverErrors,
} from '../utilities/application/coverHelper';

const enquiry = {
  id: null,
  applicantId: null,
  branch: '',
  tag: '',
  locale: '',
  sections: [],
  allAnswers: {},
  availableConditions: [],
  exclusives: [],
  loading: false,
  error: false,
};

const status = {
  applicantId: null,
  loading: false,
  isSatisfied: false,
  error: false,
  pristine: true,
};

export const initialData = {
  id: null,
  loading: false,
  error: false,
  status: null,
  applicationStatus: null,
  isQuoteEditable: null,
  isMortgageRelated: null,
  isAdvisedSale: null,
  thirdPartyRef: '',
  initialCommissionSacrificePercentage: null,
  initialIndemnityPercentage: null,
  policyStartDateOption: null,
  policyStartDate: null,
  validUntil: null,
  active: false,
  isRestrictedCover: false,
  children: [],
  hasUnbornChildren: null,
  bankDetails: null,
  candidateBeneficiaries: [],
  adviserAtFirmLocationId: null,
  isCustomerPrivacyPolicySubmitted: false,
  quoteAgeDate: null,
  extendedOnDate: null,
  isPortalQuote: false,
  applicants: [],
  applicantStatus: {
    applicants: [{ ...status }, { ...status }],
    cover: {
      loading: false,
      error: false,
    },
    contact: {
      loading: false,
      error: false,
    },
    decision: {
      loading: false,
      error: false,
    },
    activate: {
      loading: false,
      error: false,
    },
    status: {
      loading: false,
      error: false,
    },
    children: {
      loading: false,
      error: false,
    },
    bankDetails: {
      loading: false,
      error: false,
      validating: false,
    },
    payoutPlanner: {
      loading: false,
      error: false,
    },
    beneficiaries: {
      loading: false,
      error: false,
    },
    underwritingUpdates: {
      loading: false,
      error: false,
    },
    quoteDate: {
      loading: false,
      error: false,
    },
    startApplication: {
      loading: false,
      error: false,
    },
    hasUnbornChildren: {
      loading: false,
      error: false,
    },
    portal: {
      applicationId: null,
      loading: false,
      error: false,
    },
    requote: {
      loading: false,
      error: false,
    },
    unenriched: {
      loading: false,
      error: false,
    },
    isTermExtensible: {
      loading: false,
      error: false,
    },
    declarationhHealthQuestions: {
      loading: false,
      error: false,
    },
    completeTermExtension: {
      loading: false,
      error: false,
      status: null,
    },
    amraConsentGranted: {
      loading: false,
      error: false,
    },
  },
  enquiry: {
    id: null,
    branch: '',
    tag: '',
    locale: '',
    sections: [],
    allAnswers: {},
    availableConditions: [],
    exclusives: [],
    loading: false,
    error: false,
    tabsComplete: false,
  },
  applicantsEnquiries: [{ ...enquiry }, { ...enquiry }],
  section: {
    selected: app_sections[0],
  },
  sections: getNavigationSections(),
  applicantsUnderwritingUpdates: [],
  applicationUnenriched: null,
  isTermExtensible: false,
  termExtensible: null,
};

export const fetchCreateApplication = createAsyncThunk(
  app_thunks.APP_FETCH_CREATE_APPLICATION,
  async () => {
    const response = await application.postCreateApplication();
    return response.data;
  }
);

export const postStartApplication = createAsyncThunk(
  app_thunks.APP_START_APPLICATION,
  async (data, { rejectWithValue }) => {
    try {
      let adviserId;
      if (data && data.adviserId) {
        adviserId = data.adviserId;
      }
      const response = await application.postStartApplication(adviserId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplyApplication = createAsyncThunk(
  app_thunks.APP_POST_APPLY_APPLICATION,
  async (applicationId) => {
    const response = await application.postApplyApplication(applicationId);
    return response.data;
  }
);

export const getApplication = createAsyncThunk(
  app_thunks.APP_GET_APPLICATION,
  async (applicationId) => {
    const response = await application.getApplication(applicationId);
    return response.data;
  }
);

export const getApplicationUnenriched = createAsyncThunk(
  app_thunks.APP_GET_APPLICATION_UNENRICHED,
  async (applicationId) => {
    const response = await application.getApplication(applicationId);
    return response.data;
  }
);

export const getApplicationWithDecisions = createAsyncThunk(
  app_thunks.APP_GET_APPLICATION_WITH_DECISIONS,
  async (data, { getState }) => {
    const response = await application.getApplicationWithDecisions(
      getState().application.id
    );
    return response.data;
  }
);

export const postAddApplicant = createAsyncThunk(
  app_thunks.APP_POST_ADD_APPLICANT,
  async (data) => {
    const response = await application.postAddApplicant(data);
    return response.data;
  }
);

export const patchApplicantById = createAsyncThunk(
  app_thunks.APP_PATCH_APPLICANT_BY_APPLICANT_ID,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.patchApplicantById(
        getState().application.id,
        getApplicantByApplicantId(getState(), data.applicantId)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchApplicantName = createAsyncThunk(
  app_thunks.APP_PATCH_APPLICANT_NAME,
  async ({ applicantId, data }, { rejectWithValue }) => {
    try {
      const response = await application.patchApplicantName(applicantId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicantById = createAsyncThunk(
  app_thunks.APP_POST_APPLICANT_BY_APPLICANT_ID,
  async (data, store) => {
    const response = await application.postApplicantById(
      store.getState().application.id,
      getApplicantByApplicantId(store.getState(), data.applicantId)
    );
    return response.data;
  }
);

export const fetchApplicationById = createAsyncThunk(
  app_thunks.APP_FETCH_APPLICATION_BY_ID,
  async (applicationId) => {
    const response = await application.fetchApplicationById(applicationId);
    return response.data;
  }
);

export const postApplicantCover = createAsyncThunk(
  app_thunks.APP_POST_APPLICANT_COVER,
  async (data, { getState, rejectWithValue }) => {
    try {
      const coverResponse = await application.postApplicantCover(
        getState().application.id,
        data
      );
      const response = await application.getApplicantCovers(data.applicantId);
      return { cover: coverResponse.data.data, covers: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchApplicantDecisionCover = createAsyncThunk(
  app_thunks.APP_PATCH_APPLICANT_DECISION_COVER,
  async ({ applicantId, cover }, { getState, rejectWithValue }) => {
    try {
      const response = await application.patchUpsellApplicantCover(
        getState().application.id,
        applicantId,
        setEmptyStringPropertiesToNull(cover)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchApplicantCover = createAsyncThunk(
  app_thunks.APP_PATCH_APPLICANT_COVER,
  async (data, { getState, rejectWithValue }) => {
    try {
      const coverResponse = await application.patchApplicantCover(
        getState().application.id,
        data
      );
      const response = await application.getApplicantCovers(data.applicantId);
      return [coverResponse.data.data, ...response.data.data];
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicantCoverIsPayoutPlannerUsed = createAsyncThunk(
  app_thunks.APP_POST_APPLICANT_COVER_ISPAYOUTPLANNERUSED,
  async (
    { applicantId, coverId, isPayoutPlannerUsed },
    { rejectWithValue }
  ) => {
    try {
      const response = await application.postApplicantCoverIsPayoutPlannerUsed(
        applicantId,
        coverId,
        isPayoutPlannerUsed
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteApplicantCover = createAsyncThunk(
  app_thunks.APP_DELETE_APPLICANT_COVER,
  async (data, { getState, rejectWithValue }) => {
    try {
      await application.deleteApplicantCover(getState().application.id, data);
      const response = await application.getApplicantCovers(data.applicantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteApplicantDecisionCover = createAsyncThunk(
  app_thunks.APP_DELETE_APPLICANT_DECISION_COVER,
  async (data, { getState, rejectWithValue }) => {
    try {
      await application.deleteApplicantCover(getState().application.id, data);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getApplicationValidateCovers = createAsyncThunk(
  app_thunks.APP_VALIDATE_APPLICANTS_COVERS,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.getApplicationValidateCovers(
        getState().application.id
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postAcceptPreUnderwritingDeclarationById = createAsyncThunk(
  app_thunks.APP_POST_ACCEPT_PRE_UNDERWRITING_DECLARATION_BY_ID,
  async (applicantId, store) => {
    const state = store.getState();
    const response = await application.postAcceptPreUnderwritingDeclarationById(
      state.application.id,
      applicantId
    );
    return response.data;
  }
);

export const postApplicationThirdPartyReference = createAsyncThunk(
  app_thunks.APP_POST_THIRD_PARTY_REFERENCE,
  async (thirdPartyRef, { getState, rejectWithValue }) => {
    try {
      const response = await application.postApplicationThirdPartyReference(
        getState().application.id,
        thirdPartyRef
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationCommission = createAsyncThunk(
  app_thunks.APP_POST_COMMISSION,
  async (
    { initialCommissionSacrificePercentage, initialIndemnityPercentage },
    { getState, rejectWithValue }
  ) => {
    try {
      const response = await application.postApplicationCommission(
        getState().application.id,
        initialCommissionSacrificePercentage,
        initialIndemnityPercentage
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationQuoteDate = createAsyncThunk(
  app_thunks.APP_POST_APPLICATION_QUOTE_DATE,
  async ({ quoteValidUntilDate }, { getState, rejectWithValue }) => {
    try {
      const response = await application.postApplicationQuoteDate(
        getState().application.id,
        quoteValidUntilDate
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationMortgageRelated = createAsyncThunk(
  app_thunks.APP_POST_MORTGAGE_RELATED,
  async (isMortgageRelated, { getState, rejectWithValue }) => {
    try {
      const response = await application.postApplicationMortgageRelated(
        getState().application.id,
        isMortgageRelated
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationIsAdvisedSale = createAsyncThunk(
  app_thunks.APP_POST_IS_ADVISED_SALE,
  async (isAdvisedSale, { getState, rejectWithValue }) => {
    try {
      const response = await application.postApplicationIsAdvisedSale(
        getState().application.id,
        isAdvisedSale
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationPolicyStartDate = createAsyncThunk(
  app_thunks.APP_POST_POLICY_START_DATE,
  async (
    { policyStartDate, policyStartDateOption },
    { getState, rejectWithValue }
  ) => {
    try {
      const response = await application.postApplicationPolicyStartDate(
        getState().application.id,
        policyStartDate,
        policyStartDateOption
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteApplicantById = createAsyncThunk(
  app_thunks.APP_DELETE_APPLICANT,
  async (applicantId, { getState, rejectWithValue }) => {
    try {
      const response = await application.deleteApplicantById(
        getState().application.id,
        applicantId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postActivateApplication = createAsyncThunk(
  app_thunks.APP_POST_ACTIVATE_APPLICATION,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.postActivateApplication(
        getState().application.id
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getApplicationStatus = createAsyncThunk(
  app_thunks.APP_GET_APPLICATION_STATUS,
  async (data, { getState }) => {
    const response = await application.getApplicationStatus(
      getState().application.id
    );
    return response.data;
  }
);

export const getApplicationPageStatus = createAsyncThunk(
  app_thunks.APP_GET_APPLICATION_PAGE_STATUS,
  async (data, { getState }) => {
    const response = await application.getApplicationStatus(
      getState().application.id
    );
    return response.data;
  }
);

export const fetchApplicantContact = createAsyncThunk(
  app_thunks.APP_FETCH_APPLICANT_CONTACT,
  async ({ applicantId }, { getState, rejectWithValue }) => {
    try {
      const response = await application.fetchApplicantContact(
        getState().application.id,
        applicantId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postSameAddressApplicantId = createAsyncThunk(
  app_thunks.APP_POST_SAME_ADDRESS_APPLICANT_ID,
  async ({ applicantId, sameAddressApplicantId }, { rejectWithValue }) => {
    try {
      const response = await application.postSameAddressApplicantId(
        applicantId,
        sameAddressApplicantId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicantContact = createAsyncThunk(
  app_thunks.APP_POST_APPLICANT_CONTACT,
  async ({ applicantId, consent }, { getState, rejectWithValue }) => {
    try {
      const index = getState().application.applicants.findIndex(
        (applicant) => applicantId === applicant.id
      );
      const sameAddressApplicantId =
        getState().application.applicants[index].sameAddressApplicantId;

      if (consent) {
        await application.postCustomerPrivacyPolicy(
          getState().application.id,
          true
        );
      }

      if (sameAddressApplicantId) {
        const otherIndex = getState().application.applicants.findIndex(
          (applicant) => sameAddressApplicantId === applicant.id
        );
        if (otherIndex !== -1) {
          const response = await application.postApplicantContact(
            getState().application.id,
            applicantId,
            {
              ...getState().application.applicants[index].contactDetails,
              ...{
                address1:
                  getState().application.applicants[otherIndex].contactDetails
                    .address1,
                address2:
                  getState().application.applicants[otherIndex].contactDetails
                    .address2,
                address3:
                  getState().application.applicants[otherIndex].contactDetails
                    .address3,
                cityName:
                  getState().application.applicants[otherIndex].contactDetails
                    .cityName,
                county:
                  getState().application.applicants[otherIndex].contactDetails
                    .county,
                postcode:
                  getState().application.applicants[otherIndex].contactDetails
                    .postcode,
              },
            }
          );
          return response.data;
        }
      }
      const response = await application.postApplicantContact(
        getState().application.id,
        applicantId,
        getState().application.applicants[index].contactDetails
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationChild = createAsyncThunk(
  app_thunks.APP_POST_APPLICATION_CHILD,
  async (child, { getState }) => {
    const response = await application.postApplicationChild(
      getState().application.id,
      child
    );
    return response.data;
  }
);

export const putApplicationChild = createAsyncThunk(
  app_thunks.APP_PUT_APPLICATION_CHILD,
  async ({ id, child }, { getState }) => {
    const response = await application.putApplicationChild(
      getState().application.id,
      id,
      child
    );
    return response.data;
  }
);

export const deleteApplicationChild = createAsyncThunk(
  app_thunks.APP_DELETE_APPLICATION_CHILD,
  async ({ id, data }, { getState, rejectWithValue }) => {
    try {
      const response = await application.deleteApplicationChild(
        getState().application.id,
        id,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getBankDetails = createAsyncThunk(
  app_thunks.APP_GET_BANK_DETAILS,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.getBankDetails(
        getState().application.id
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postBankDetails = createAsyncThunk(
  app_thunks.APP_POST_BANK_DETAILS,
  async ({ bankDetails }, { getState, rejectWithValue }) => {
    try {
      const response = await application.postBankDetails(
        getState().application.id,
        {
          ...getState().application.bankDetails,
          ...bankDetails,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postValidateBankDetails = createAsyncThunk(
  app_thunks.APP_POST_VALIDATE_BANK_DETAILS,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.postValidateBankDetails(
        getState().application.id
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postOverrideBankValidation = createAsyncThunk(
  app_thunks.APP_POST_OVERRIDE_BANK_VALIDATION,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.postOverrideBankValidation(
        getState().application.id
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postResetBankValidation = createAsyncThunk(
  app_thunks.APP_POST_RESET_BANK_VALIDATION,
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await application.postBankDetails(
        getState().application.id,
        {
          ...getState().application.bankDetails,
          bankCheckValidation: null,
          bankCheckResult: null,
          bankCheckStatus: null,
          isBankCheckConfirmed: null,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationCoverBeneficiary = createAsyncThunk(
  app_thunks.APP_POST_APPLICATION_BENEFICIARY,
  async ({ beneficiary }, { getState, rejectWithValue }) => {
    try {
      const response = await application.postApplicationCoverBeneficiary(
        getState().application.id,
        beneficiary
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const putApplicationCoverBeneficiary = createAsyncThunk(
  app_thunks.APP_PUT_APPLICATION_BENEFICIARY,
  async ({ beneficiaryId, beneficiary }, { getState, rejectWithValue }) => {
    try {
      const response = await application.putApplicationCoverBeneficiary(
        getState().application.id,
        beneficiaryId,
        beneficiary
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationCoverBeneficiaryAllocation = createAsyncThunk(
  app_thunks.APP_POST_APPLICANT_COVER_BENEFICIARY_ALLOCATION,
  async (
    { applicantId, coverId, referenceId, type, percent },
    { getState, rejectWithValue }
  ) => {
    try {
      const response =
        await application.postApplicationCoverBeneficiaryAllocation(
          getState().application.id,
          applicantId,
          coverId,
          referenceId,
          type,
          percent
        );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getIsTermExtensible = createAsyncThunk(
  app_thunks.APP_GET_ISTERMEXTENSIBLE,
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await application.getIsTermExtensible(applicationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const hasFailedTermExtension = createAsyncThunk(
  app_thunks.APP_POST_HASFAILEDTERMEXTENSION,
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await application.hasFailedTermExtension(applicationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const triggerDeclarationOfHealthQuestions = createAsyncThunk(
  app_thunks.APP_GET_DECLARATION_OF_HEALTH_QUESTIONS,
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await application.triggerDeclarationOfHealthQuestions(
        applicationId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const completeTermExtension = createAsyncThunk(
  app_thunks.APP_POST_COMPLETE_TERM_EXTENSION,
  async ({ applicationId, applicants }) => {
    const response = await application.completeTermExtension(
      applicationId,
      applicants
    );
    return response.data;
  }
);

export const putApplicationCoverBeneficiaryAllocation = createAsyncThunk(
  app_thunks.APP_PUT_APPLICANT_COVER_BENEFICIARY_ALLOCATION,
  async (
    {
      applicantId,
      coverId,
      beneficiaryAllocationId,
      referenceId,
      type,
      percent,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const response =
        await application.putApplicationCoverBeneficiaryAllocation(
          getState().application.id,
          applicantId,
          coverId,
          beneficiaryAllocationId,
          referenceId,
          type,
          percent
        );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteApplicationCoverBeneficiaryAllocation = createAsyncThunk(
  app_thunks.APP_DELETE_APPLICANT_COVER_BENEFICIARY_ALLOCATION,
  async (
    { applicantId, coverId, beneficiaryAllocationId },
    { getState, rejectWithValue }
  ) => {
    try {
      const response =
        await application.deleteApplicationCoverBeneficiaryAllocation(
          getState().application.id,
          applicantId,
          coverId,
          beneficiaryAllocationId
        );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationDecision = createAsyncThunk(
  app_thunks.APP_POST_DECISION,
  async ({ applicantId }, { rejectWithValue }) => {
    try {
      const response = await application.postApplicationDecision(applicantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postApplicationQiDecision = createAsyncThunk(
  app_thunks.APP_POST_DECISION,
  async ({ applicantId }, { rejectWithValue }) => {
    try {
      const response = await application.postApplicationQiDecision(applicantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const openApplicationAnswersDocument = createAsyncThunk(
  app_thunks.APP_OPEN_APPLICATION_ANSWERS_DOCUMENT,
  async ({
    applicationId,
    applicantId,
    interviewId,
    interviewToken,
    adviserId,
  }) => {
    const response = await application.openApplicationAnswersDocument(
      applicationId,
      applicantId,
      interviewId,
      interviewToken,
      adviserId
    );
    return response;
  }
);

export const openDecisionDocument = createAsyncThunk(
  app_thunks.APP_OPEN_DECISION_DOCUMENT,
  async ({ applicationId, interviewId, interviewToken, adviserId }) => {
    const response = await application.openDecisionDocument(
      applicationId,
      interviewId,
      interviewToken,
      adviserId
    );
    return response;
  }
);

export const openKeyFactsDocument = createAsyncThunk(
  app_thunks.APP_OPEN_KEYFACTS_DOCUMENT,
  async ({ url }, { rejectWithValue }) => {
    try {
      const response = await application.openDocumentByUrl(url);
      return response.data.data.uri;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const openTermsAndConditionsDocument = createAsyncThunk(
  app_thunks.APP_OPEN_TERMSANDCONDITIONS_DOCUMENT,
  async ({ url }, { rejectWithValue }) => {
    try {
      const response = await application.openDocumentByUrl(url);
      return response.data.data.uri;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getApplicationUnderwritingUpdates = createAsyncThunk(
  app_thunks.APP_GET_UNDERWRITING_UPDATES,
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await application.getApplicationUnderwritingUpdates(
        applicationId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postHasUnbornChildren = createAsyncThunk(
  app_thunks.APP_POST_HASUNBORNCHILDREN,
  async ({ hasUnbornChildren }, { getState, rejectWithValue }) => {
    try {
      const response = await application.postHasUnbornChildren(
        getState().application.id,
        hasUnbornChildren
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postPortalApply = createAsyncThunk(
  app_thunks.APP_POST_PORTALAPPLY,
  async ({ portalId }, { rejectWithValue }) => {
    try {
      const response = await application.postPortalApply(portalId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postUnlockQuoteEdit = createAsyncThunk(
  app_thunks.APP_POST_UNLOCKQUOTEEDIT,
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const response = await application.postUnlockQuoteEdit(applicationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchContactDetails = createAsyncThunk(
  app_thunks.APP_PATCH_APPLICANT_CONTACT,
  async ({ applicantId, data }, { rejectWithValue }) => {
    try {
      const response = await application.patchContactDetails(applicantId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postAmraConsentGranted = createAsyncThunk(
  app_thunks.APP_POST_AMRA_CONSENT,
  async ({ applicantId, isAmraConsentGranted }, { rejectWithValue }) => {
    try {
      const response = await application.postAmraConsentGranted(
        applicantId,
        isAmraConsentGranted
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postViewMedicalReportsRequested = createAsyncThunk(
  app_thunks.APP_POST_AMRA_MEDICAL_REPORTS,
  async (
    { applicantId, isViewMedicalReportsRequested },
    { rejectWithValue }
  ) => {
    try {
      const response = await application.postViewMedicalReportsRequested(
        applicantId,
        isViewMedicalReportsRequested
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const patchApplicantExaminerDetails = createAsyncThunk(
  app_thunks.APP_PATCH_APPLICANT_EXAMINER_DETAILS,
  async ({ applicantId, data }, { rejectWithValue }) => {
    try {
      const response = await application.patchApplicantExaminerDetails(
        applicantId,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const applicationSlice = createSlice({
  name: app_slice_name,
  initialState: initialData,
  reducers: {
    reset() {
      return initialData;
    },
    setApplicant: (state, action) => {
      state.applicants.push(action.payload);
    },
    setApplicantData: (state, action) => {
      const index = getApplicantIndexFromPayload(state, action);
      if (index !== -1) {
        state.applicants[index] = {
          ...state.applicants[index],
          ...action.payload,
        };
        state.applicantStatus.applicants[index].pristine = false;
        state.applicantStatus.applicants[index].error = false;
      }
    },
    addApplicantCover: (state, action) => {
      const index = getApplicantIndexFromPayload(state, action);
      // Create a reference id so we can manage the state of unsaved covers
      const ref = uuidv4();
      state.applicants[index].covers.push({
        productType: action.payload.productType,
        ...action.payload?.defaults,
        ref,
      });
    },
    updateApplicantCover: (state, action) => {
      const { id } = action.payload;
      const index = getApplicantIndexFromPayload(state, action);
      const coverIndex = current(state.applicants[index].covers).findIndex(
        (cover) => cover.id === id
      );
      state.applicants[index].covers[coverIndex] = action.payload;
    },
    updateApplicantDecisionCover: (state, action) => {
      const { id, productType } = action.payload;
      const index = getApplicantIndexFromPayload(state, action);
      const coverIndex = current(state.applicants[index].covers).findIndex(
        (cover) => cover.id === id
      );
      state.applicants[index].covers[coverIndex] = action.payload;

      state.applicants[index].covers = current(
        state.applicants[index].covers
      ).map((cover) => {
        if (cover === id) {
          return { ...cover, ...action.payload };
        } else {
          if (
            productType === application_cover_products.CCIC &&
            cover.riders &&
            cover.riders.length > 0
          ) {
            return { ...cover, riders: [action.payload] };
          }
          return cover;
        }
      });
    },
    removeApplicantCover: (state, action) => {
      const { index: coverIndex } = action.payload;
      const index = getApplicantIndexFromPayload(state, action);
      state.applicants[index].covers.splice(coverIndex, 1);
    },
    setApplicant1EnquiryId(state, action) {
      state.applicantsEnquiries[0].id = action.payload;
    },
    setApplicant2EnquiryId(state, action) {
      state.applicantsEnquiries[1].id = action.payload;
    },
    setMortgageRelated(state, action) {
      state.isMortgageRelated = action.payload;
    },
    setCustomerPrivacyPolicySubmitted(state, action) {
      state.isCustomerPrivacyPolicySubmitted = action.payload;
    },
    setApplicantContactDetails(state, action) {
      const index = getApplicantIndexFromPayload(state, action);
      const { data } = action.payload;
      state.applicants[index].contactDetails = data;
    },
    setApplicantSameAddressId(state, action) {
      const { sameAddressApplicantId } = action.payload;
      const index = getApplicantIndexFromPayload(state, action);
      if (index !== -1) {
        state.applicants[index].sameAddressApplicantId = sameAddressApplicantId;
      }
    },
    setApplicantPristine(state, action) {
      const index = getApplicantIndexFromPayload(state, action);
      if (index !== -1) {
        state.applicantStatus.applicants[index].pristine =
          action.payload.pristine;
      }
    },
    removeApplicant: (state, action) => {
      const index = getApplicantIndexFromPayload(state, action);
      if (index !== -1) {
        state.applicants.splice(index, 1);
      }
    },
    setBankDetailsInvalid: (state) => {
      try {
        if (
          current(state.bankDetails) &&
          current(state.bankDetails).bankCheckResult
        ) {
          state.bankDetails.bankCheckResult = null;
        }
      } catch {
        //
      }
    },
    setTermExtensibleAnswer: (state, action) => {
      const { index, answer } = action.payload;
      try {
        state.termExtensible.applicantEnquiries[index].enquiry.allAnswers = {
          ...state.termExtensible.applicantEnquiries[index].enquiry.allAnswers,
          ...answer,
        };
      } catch {
        //
      }
    },
    resetTermExtensible: (state) => {
      state.termExtensible = null;
      state.applicantStatus.declarationhHealthQuestions = {
        loading: false,
        error: null,
      };
      state.applicantStatus.isTermExtensibles = { loading: false, error: null };
      state.applicantStatus.completeTermExtension = {
        loading: false,
        error: null,
        status: null,
      };
    },
    setExclusive(state, action) {
      const index = state.termExtensible.applicantEnquiries.findIndex(
        (applicant) => applicant.applicantId === action.payload.applicantId
      );
      if (index !== -1) {
        const exclusives = [
          ...(state.termExtensible.applicantEnquiries[index].enquiry
            ?.exclusives ?? []),
          action.payload.data,
        ];
        state.termExtensible.applicantEnquiries[index].enquiry.exclusives =
          exclusives;
      }
    },
    deleteExclusive(state, action) {
      const index = state.termExtensible.applicantEnquiries.findIndex(
        (applicant) => applicant.applicantId === action.payload.applicantId
      );
      if (index !== -1) {
        const exclusives = current(
          state.termExtensible.applicantEnquiries[index].enquiry.exclusives
        ).filter((item) => {
          try {
            return Object.keys(item)[0] !== Object.keys(action.payload.data)[0];
          } catch {
            return false;
          }
        });
        state.termExtensible.applicantEnquiries[index].enquiry.exclusives =
          exclusives;
      }
    },
    updateApplicantExaminer(state, action) {
      const index = getApplicantIndexFromPayload(state, action);
      if (index !== -1 && action.payload.examiner) {
        state.applicants[index].examiner = {
          title: action.payload.examiner.title,
          forename: action.payload.examiner.forename,
          surname: action.payload.examiner.surname,
          organisationName: action.payload.examiner.organisationName,
          isExaminerDetailsValid:
            action.payload.examiner.isExaminerDetailsValid,
          contactDetails: {
            address1: action.payload.examiner.address1,
            address2: action.payload.examiner.address2,
            address3: action.payload.examiner.address3,
            cityName: action.payload.examiner.cityName,
            county: action.payload.examiner.county,
            emailAddress: action.payload.examiner.emailAddress,
            phoneNumber: action.payload.examiner.phoneNumber,
            postcode: action.payload.examiner.postcode,
          },
        };
      }
      if (index !== -1 && action.payload.examiner === null) {
        state.applicants[index].examiner = null;
      }
    },
  },
  extraReducers: {
    [fetchCreateApplication.pending]: (state) => {
      state.loading = true;
    },
    [fetchCreateApplication.fulfilled]: (state, action) => {
      const {
        data: { id },
      } = action.payload;
      state.id = id;
      state.loading = false;
    },
    [fetchCreateApplication.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [postStartApplication.pending]: (state) => {
      state.loading = true;
    },
    [postStartApplication.fulfilled]: (state, action) => {
      const { applicantId, branch, tag, locale, sections, allAnswers } =
        action.payload.data.enquiry;
      state.id = action.payload.data.application.id;
      state.status = action.payload.data.status;
      state.applicants = action.payload.data.application.applicants;
      state.initialCommissionSacrificePercentage =
        action.payload.data.application.initialCommissionSacrificePercentage;
      state.initialIndemnityPercentage =
        action.payload.data.application.initialIndemnityPercentage;
      if (action.payload.data.children) {
        state.children = action.payload.data.children;
      }
      state.quoteAgeDate = action.payload.data.application.quoteAgeDate;
      action.payload.data.application.applicants.forEach((applicant, index) => {
        state.applicantsEnquiries[index].id = applicantId;
        state.applicantsEnquiries[index].branch = branch;
        state.applicantsEnquiries[index].tag = tag;
        state.applicantsEnquiries[index].locale = locale;
        state.applicantsEnquiries[index].sections = sections;
        state.applicantsEnquiries[index].loading = false;
        state.applicantsEnquiries[index].allAnswers = allAnswers;
        state.applicantStatus.applicants[index].applicantId = applicantId;
        state.loading = false;
      });
    },
    [postStartApplication.rejected]: (state, action) => {
      state.applicantStatus.startApplication.error = action.error.message;
      state.loading = false;
    },
    [getApplication.pending]: (state) => {
      state.loading = true;
    },
    [getApplication.fulfilled]: (state, action) => {
      state.id = action.payload?.data?.id;
      state.thirdPartyRef = action.payload?.data?.thirdPartyRef;
      state.isMortgageRelated = action.payload?.data?.isMortgageRelated;
      state.isAdvisedSale = action.payload?.data?.isAdvisedSale;
      state.policyStartDate = action.payload?.data?.policyStartDate;
      state.policyStartDateOption = action.payload?.data?.policyStartDateOption;
      state.status = action.payload?.data?.status;
      state.isRestrictedCover = action.payload?.data?.isRestrictedCover;
      state.bankDetails = action.payload?.data?.bankDetails;
      state.candidateBeneficiaries =
        action.payload?.data?.candidateBeneficiaries;
      state.initialCommissionSacrificePercentage =
        action.payload?.data?.initialCommissionSacrificePercentage;
      state.initialIndemnityPercentage =
        action.payload?.data?.initialIndemnityPercentage;
      state.adviserAtFirmLocationId =
        action.payload?.data?.adviserAtFirmLocationId;
      state.hasUnbornChildren = action.payload?.data?.hasUnbornChildren;
      state.quoteAgeDate = action.payload?.data?.quoteAgeDate;
      state.extendedOnDate = action.payload?.data?.extendedOnDate;
      state.isCustomerPrivacyPolicySubmitted =
        action.payload?.data?.isCustomerPrivacyPolicySubmitted;
      state.isPortalQuote =
        action.payload?.data?.portal === 'Protection Platform' ? true : false;
      if (action.payload?.data?.children) {
        state.children = action.payload?.data?.children;
      }
      action.payload?.data?.applicants.forEach((applicant, index) => {
        state.applicants[index] = {
          ...applicant,
          covers: sortApplicantCovers(applicant.covers),
        };
        state.applicantsEnquiries[index].id = applicant.id;
        state.applicantsEnquiries[index].loading = false;
        state.applicantStatus.applicants[index].applicantId = applicant.id;
        state.applicantStatus.applicants[index].isSatisfied =
          isApplicantStatisfied(applicant);
        state.loading = false;
      });
    },
    [getApplication.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [getApplicationWithDecisions.pending]: (state) => {
      state.loading = true;
    },
    [getApplicationWithDecisions.fulfilled]: (state, action) => {
      state.id = action.payload.data.application.id;
      state.thirdPartyRef = action.payload.data.application.thirdPartyRef;
      state.isMortgageRelated =
        action.payload.data.application.isMortgageRelated;
      state.isAdvisedSale = action.payload.data.application.isAdvisedSale;
      state.policyStartDate = action.payload.data.application.policyStartDate;
      state.policyStartDateOption =
        action.payload.data.application.policyStartDateOption;
      state.status = action.payload.data.application.status;
      state.isRestrictedCover =
        action.payload.data.application.isRestrictedCover;
      state.bankDetails = action.payload.data.application.bankDetails;
      state.candidateBeneficiaries =
        action.payload.data.application.candidateBeneficiaries;
      state.initialCommissionSacrificePercentage =
        action.payload.data.application.initialCommissionSacrificePercentage;
      state.initialIndemnityPercentage =
        action.payload.data.application.initialIndemnityPercentage;
      state.adviserAtFirmLocationId =
        action.payload.data.application.adviserAtFirmLocationId;
      state.hasUnbornChildren =
        action.payload.data.application.hasUnbornChildren;
      state.isCustomerPrivacyPolicySubmitted =
        action.payload.data.application.isCustomerPrivacyPolicySubmitted;
      state.quoteAgeDate = action.payload.data.application.quoteAgeDate;
      state.extendedOnDate = action.payload.data.application.extendedOnDate;
      state.isPortalQuote =
        action.payload?.data?.application?.portal === 'Protection Platform'
          ? true
          : false;
      if (action.payload.data.application.children) {
        state.children = action.payload.data.application.children;
      }
      action.payload.data.application.applicants.forEach((applicant, index) => {
        state.applicants[index] = {
          ...applicant,
          covers: sortApplicantCovers(applicant.covers),
        };
        state.applicantsEnquiries[index].id = applicant.id;
        state.applicantsEnquiries[index].loading = false;
        state.applicantStatus.applicants[index].applicantId = applicant.id;
        state.applicantStatus.applicants[index].isSatisfied =
          isApplicantStatisfied(applicant);
        state.loading = false;
      });
    },
    [getApplicationWithDecisions.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },

    [postApplyApplication.pending]: (state) => {
      state.loading = true;
    },
    [postApplyApplication.fulfilled]: (state, action) => {
      state.id = action.payload.data.id;
      state.thirdPartyRef = action.payload.data.thirdPartyRef;
      state.isMortgageRelated = action.payload.data.isMortgageRelated;
      state.isAdvisedSale = action.payload.data.isAdvisedSale;
      state.status = action.payload.data.status;
      state.isRestrictedCover = action.payload.data.isRestrictedCover;
      state.initialCommissionSacrificePercentage =
        action.payload.data.initialCommissionSacrificePercentage;
      state.initialIndemnityPercentage =
        action.payload.data.initialIndemnityPercentage;
      state.adviserAtFirmLocationId =
        action.payload.data.adviserAtFirmLocationId;
      action.payload.data.applicants.forEach((applicant, index) => {
        state.applicantsEnquiries[index].id = applicant.id;
        state.applicantsEnquiries[index].loading = false;
        state.applicantStatus.applicants[index].applicantId = applicant.id;
        state.loading = false;
        state.applicants[index] = {
          ...applicant,
          covers: sortApplicantCovers(applicant.covers),
        };
      });
    },
    [postApplyApplication.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [postAddApplicant.pending]: (state) => {
      state.applicantStatus.applicants[1].loading = true;
    },
    [postAddApplicant.fulfilled]: (state, action) => {
      const { applicantId, branch, tag, locale, sections, allAnswers } =
        action.payload.data.enquiry;
      state.applicantsEnquiries[1].id = applicantId;
      state.applicantsEnquiries[1].branch = branch;
      state.applicantsEnquiries[1].tag = tag;
      state.applicantsEnquiries[1].locale = locale;
      state.applicantsEnquiries[1].sections = sections;
      state.applicantsEnquiries[1].loading = false;
      state.applicantsEnquiries[1].allAnswers = allAnswers;
      state.applicantStatus.applicants[1].applicantId = applicantId;
      state.applicantStatus.applicants[1].loading = false;
      state.applicants.push(action.payload.data.applicant);
    },
    [postAddApplicant.rejected]: (state, action) => {
      state.applicantStatus.applicants[1].error = action.error.message;
      state.applicantStatus.applicants[1].loading = false;
    },
    [patchApplicantById.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicantStatus.applicants[index].loading = true;
    },
    [patchApplicantById.fulfilled]: (state, action) => {
      const { data } = action.payload;
      const index = getApplicantIndexFromPayloadData(state, action);
      state.applicantStatus.applicants[index].loading = false;
      state.applicantStatus.applicants[index].isSatisfied = true;
      state.applicantStatus.applicants[index].error = false;
      state.applicants[index] = {
        ...data,
        covers: sortApplicantCovers(data.covers),
      };
    },
    [patchApplicantById.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicantStatus.applicants[index].loading = false;
      state.applicantStatus.applicants[index].isSatisfied = false;
      state.applicantStatus.applicants[index].error =
        action.payload?.errors ?? 'Error';
    },
    [patchApplicantName.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicantStatus.applicants[index].loading = true;
    },
    [patchApplicantName.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromPayloadData(state, action);
      try {
        const {
          arg: { data },
        } = action.meta;
        Object.keys(data).map(
          (key) => (state.applicants[index][key] = action.payload.data[key])
        );
      } catch {
        //
      }
      state.applicantStatus.applicants[index].loading = false;
      state.applicantStatus.applicants[index].error = false;
    },
    [patchApplicantName.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicantStatus.applicants[index].loading = false;
      try {
        state.applicantStatus.applicants[index].error = Object.keys(
          action.payload?.errors?.errors
        )
          .map((key) => {
            return {
              atteptedValue: Object.keys(action.meta.arg.data).find(
                (field) => field.toLowerCase() === key.toLowerCase()
              ),
              errorMessage: action.payload?.errors?.errors[key],
              propertyName: key,
            };
          })
          .filter((error) => !!error.atteptedValue);
      } catch {
        //
      }

      try {
        const {
          arg: { data },
        } = action.meta;
        Object.keys(data).map(
          (key) => (state.applicants[index][key] = data[key])
        );
      } catch {
        //
      }
    },
    [postApplicantById.pending]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicantStatus.applicants[index].loading = true;
      state.applicantStatus.applicants[index].error = false;
    },
    [postApplicantById.fulfilled]: (state, action) => {
      const { data } = action.payload;
      const index = getApplicantIndexFromPayloadData(state, action);
      state.applicants[index] = data;
      state.applicantStatus.applicants[index].loading = false;
      state.applicantStatus.applicants[index].isSatisfied = true;
    },
    [postApplicantById.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicantStatus.applicants[index].loading = false;
      state.applicantStatus.applicants[index].isSatisfied = false;
      state.applicantStatus.applicants[index].error =
        action.payload?.errors ?? 'Unknown error';
    },
    [fetchApplicationById.pending]: (state) => {
      state.loading = true;
    },
    [fetchApplicationById.fulfilled]: (state, action) => {
      state.id = action.payload.data.id;
      state.applicants = action.payload.data.applicants;
      state.loading = false;
    },
    [fetchApplicationById.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [postApplicantCover.pending]: (state) => {
      state.applicantStatus.cover.loading = true;
    },
    [postApplicantCover.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { index: coverIndex, productType } = action.meta.arg;
      const { cover, covers } = action.payload;
      state.applicants[index].covers[coverIndex] = cover;

      state.applicants[index].covers = current(
        state.applicants[index].covers
      ).map((currentCover) => {
        const foundCover = covers.find(
          (updatedCover) => updatedCover.id === currentCover.id
        );
        if (foundCover) {
          return { ...currentCover, ...foundCover };
        } else {
          if (
            currentCover.productType === application_cover_products.CCIC &&
            productType === application_cover_products.CCIC
          ) {
            let ccic;
            try {
              ccic = covers.find((c) => c.riders && c.riders.length > 0)
                .riders[0];
              return ccic;
            } catch {
              return currentCover;
            }
          }
          return currentCover;
        }
      });
      state.applicantStatus.cover.loading = false;
    },
    [postApplicantCover.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const {
        meta: {
          arg,
          arg: { id, index: coverIndex },
        },
      } = action;
      if (action.payload?.errors) {
        const matchIndex = state.applicants[index].covers.findIndex(
          (cover) => cover.id && cover.id === id
        );
        if (matchIndex !== -1) {
          state.applicants[index].covers[matchIndex] = arg;
          state.applicants[index].covers[matchIndex].errors =
            action.payload?.errors.reduce((unique, o) => {
              if (!unique.some((obj) => obj.errorMessage === o.errorMessage)) {
                unique.push(o);
              }
              return unique;
            }, []);
        } else {
          state.applicants[index].covers[coverIndex] = arg;
          state.applicants[index].covers[coverIndex].errors =
            action.payload?.errors.reduce((unique, o) => {
              if (!unique.some((obj) => obj.errorMessage === o.errorMessage)) {
                unique.push(o);
              }
              return unique;
            }, []);
        }
      }
      state.applicantStatus.cover.loading = false;
    },
    [patchApplicantDecisionCover.pending]: (state) => {
      state.applicantStatus.cover.loading = true;
    },
    [patchApplicantDecisionCover.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const {
        meta: {
          arg: {
            cover: { index: coverIndex },
          },
        },
      } = action;
      state.applicantStatus.cover.loading = false;
      try {
        state.applicants[index].covers[coverIndex].errors = null;
      } catch {
        //
      }
    },
    [patchApplicantDecisionCover.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const {
        meta: {
          arg,
          arg: {
            cover: { id, index: coverIndex, parentCoverId },
          },
        },
      } = action;
      if (action.payload?.errors) {
        const matchIndex = state.applicants[index].covers.findIndex(
          (cover) =>
            (cover.id && cover.id === id) ||
            (cover.id && cover.id === parentCoverId)
        );
        if (matchIndex !== -1 && !parentCoverId) {
          state.applicants[index].covers[matchIndex] = arg.cover;
          state.applicants[index].covers[matchIndex].errors =
            action.payload?.errors.reduce((unique, o) => {
              if (!unique.some((obj) => obj.errorMessage === o.errorMessage)) {
                unique.push(o);
              }
              return unique;
            }, []);
          state.applicants[index].covers[matchIndex].valid = false;
        } else if (matchIndex !== -1 && parentCoverId) {
          state.applicants[index].covers[matchIndex].riders = current(
            state.applicants[index].covers[matchIndex].riders
          ).map((rider) => {
            if (rider.id === id) {
              return {
                ...rider,
                errors: action.payload?.errors,
              };
            }
            return rider;
          });
          state.applicants[index].covers[matchIndex].valid = false;
        } else {
          state.applicants[index].covers[coverIndex] = arg.cover;
          state.applicants[index].covers[coverIndex].errors =
            action.payload?.errors.reduce((unique, o) => {
              if (!unique.some((obj) => obj.errorMessage === o.errorMessage)) {
                unique.push(o);
              }
              return unique;
            }, []);
          state.applicants[index].covers[coverIndex].valid = false;
        }
      }
      state.applicantStatus.cover.loading = false;
    },
    [patchApplicantCover.pending]: (state) => {
      state.applicantStatus.cover.loading = true;
    },
    [patchApplicantCover.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const data = action.payload;
      const { id } = action.meta.arg;

      state.applicants[index].covers = current(
        state.applicants[index].covers
      ).map((cover) => {
        const foundCover = data.find(
          (updatedCover) => updatedCover.id === cover.id
        );
        if (foundCover && id !== cover.id) {
          return { ...cover, ...foundCover };
        } else if (foundCover && id == cover.id) {
          return { ...cover, ...foundCover, errors: null };
        } else {
          return cover;
        }
      });
      state.applicantStatus.cover.loading = false;
    },
    [patchApplicantCover.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const {
        meta: {
          arg,
          arg: { id, index: coverIndex, parentCoverId },
        },
      } = action;
      if (action.payload?.errors) {
        const matchIndex = state.applicants[index].covers.findIndex(
          (cover) =>
            (cover.id && cover.id === id) ||
            (cover.id && cover.id === parentCoverId)
        );
        if (matchIndex !== -1 && !parentCoverId) {
          state.applicants[index].covers[matchIndex] = arg;
          state.applicants[index].covers[matchIndex].errors =
            action.payload?.errors?.reduce((unique, o) => {
              if (!unique.some((obj) => obj.errorMessage === o.errorMessage)) {
                unique.push(o);
              }
              return unique;
            }, []) ?? 'Error';
        } else if (matchIndex !== -1 && parentCoverId) {
          state.applicants[index].covers[matchIndex].riders = current(
            state.applicants[index].covers[matchIndex].riders
          ).map((rider) => {
            if (rider.id === id) {
              return {
                ...rider,
                errors: action.payload?.errors,
              };
            }
            return rider;
          });
        } else {
          state.applicants[index].covers[coverIndex] = arg;
          state.applicants[index].covers[coverIndex].errors =
            action.payload?.errors.reduce((unique, o) => {
              if (!unique.some((obj) => obj.errorMessage === o.errorMessage)) {
                unique.push(o);
              }
              return unique;
            }, []);
        }
      }
      state.applicantStatus.cover.loading = false;
    },
    [deleteApplicantCover.pending]: (state) => {
      state.applicantStatus.cover.loading = true;
    },
    [deleteApplicantCover.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { index: coverIndex, productType } = action.meta.arg;
      const { data } = action.payload;
      // Remove the riders of all products and remove the CICC cover
      if (productType === application_cover_products.CCIC) {
        state.applicants[index].covers = current(
          state.applicants[index].covers
        ).map((cover) => ({ ...cover, riders: [] }));
        state.applicants[index].covers.splice(coverIndex, 1);
      } else if (current(state.applicants[index].covers).length === 2) {
        // If there's only 2 covers and we're not deleting a CICC cover
        // then remove any CICC covers
        state.applicants[index].covers = current(
          state.applicants[index].covers
        ).filter((cover, index) => {
          if (
            index === coverIndex ||
            cover.productType === application_cover_products.CCIC
          ) {
            return false;
          }
          return true;
        });
      } else {
        state.applicants[index].covers.splice(coverIndex, 1);
        state.applicants[index].covers = current(
          state.applicants[index].covers
        ).map((cover) => {
          const foundCover = data.find(
            (updatedCover) => updatedCover.id === cover.id
          );
          if (foundCover) {
            return { ...cover, ...foundCover };
          } else {
            return cover;
          }
        });
      }
      state.applicantStatus.cover.loading = false;
    },
    [deleteApplicantCover.rejected]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const {
        meta: {
          arg,
          arg: { id, index: coverIndex, parentCoverId },
        },
      } = action;
      if (action.payload?.errors) {
        const matchIndex = state.applicants[index].covers.findIndex(
          (cover) =>
            (cover.id && cover.id === id) ||
            (cover.id && cover.id === parentCoverId)
        );
        if (matchIndex !== -1 && !parentCoverId) {
          state.applicants[index].covers[matchIndex] = arg;
          state.applicants[index].covers[matchIndex].errors =
            action.payload?.errors;
        } else if (matchIndex !== -1 && parentCoverId) {
          state.applicants[index].covers[matchIndex].riders = current(
            state.applicants[index].covers[matchIndex].riders
          ).map((rider) => {
            if (rider.id === id) {
              return {
                ...rider,
                errors: action.payload?.errors,
              };
            }
            return rider;
          });
        } else {
          state.applicants[index].covers[coverIndex] = arg;
          state.applicants[index].covers[coverIndex].errors =
            action.payload?.errors;
        }
      }
      state.applicantStatus.cover.error = action.error.message;
      state.applicantStatus.cover.loading = false;
    },
    [deleteApplicantDecisionCover.pending]: (state) => {
      state.applicantStatus.cover.loading = true;
    },
    [deleteApplicantDecisionCover.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      const { index: coverIndex, productType } = action.meta.arg;
      // Remove the riders of all products and remove the CICC cover
      if (productType === application_cover_products.CCIC) {
        state.applicants[index].covers = current(
          state.applicants[index].covers
        ).map((cover) => ({ ...cover, riders: [] }));
        state.applicants[index].covers.splice(coverIndex, 1);
      } else if (current(state.applicants[index].covers).length === 2) {
        // If there's only 2 covers and we're not deleting a CICC cover
        // then remove any CICC covers
        state.applicants[index].covers = current(
          state.applicants[index].covers
        ).filter((cover, index) => {
          if (
            index === coverIndex ||
            cover.productType === application_cover_products.CCIC
          ) {
            return false;
          }
          return true;
        });
      } else {
        state.applicants[index].covers.splice(coverIndex, 1);
      }
      state.applicantStatus.cover.loading = false;
    },
    [deleteApplicantDecisionCover.rejected]: (state, action) => {
      state.applicantStatus.cover.error = action.error.message;
      state.applicantStatus.cover.loading = false;
    },
    [getApplicationValidateCovers.pending]: (state) => {
      state.applicantStatus.cover.loading = true;
    },
    [getApplicationValidateCovers.fulfilled]: (state, action) => {
      action.payload?.data?.forEach((applicant, index) => {
        state.applicants[index] = {
          ...applicant,
          covers: sortApplicantCovers(applicant.covers),
        };
        state.applicantsEnquiries[index].id = applicant.id;
        state.applicantsEnquiries[index].loading = false;
        state.applicantStatus.applicants[index].applicantId = applicant.id;
        state.applicantStatus.applicants[index].isSatisfied =
          isApplicantStatisfied(applicant);
      });
      state.applicantStatus.cover.loading = false;
    },
    [getApplicationValidateCovers.rejected]: (state, action) => {
      state.applicantStatus.cover.error = action.error.message;
      state.applicantStatus.cover.loading = false;
      try {
        state.applicants = state.applicants.map((applicant) => ({
          ...applicant,
          covers: applicant.covers.map((cover) =>
            applyCoverErrors(cover, action.payload?.errors)
          ),
        }));
      } catch (error) {
        console.log(error);
      }
    },
    [postAcceptPreUnderwritingDeclarationById.pending]: (state) => {
      state.loading = true;
    },
    [postAcceptPreUnderwritingDeclarationById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].isAcceptedPreUnderwritingDeclaration = true;
      state.loading = false;
    },
    [postAcceptPreUnderwritingDeclarationById.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [postApplicationThirdPartyReference.fulfilled]: (state, action) => {
      state.thirdPartyRef = action.payload?.data?.thirdPartyRef;
    },
    [postApplicationThirdPartyReference.rejected]: (state, action) => {
      state.error = action.error.message;
    },
    [postApplicationCommission.fulfilled]: (state, action) => {
      const {
        data: {
          initialCommissionSacrificePercentage,
          initialIndemnityPercentage,
        },
      } = action.payload;
      state.initialCommissionSacrificePercentage =
        initialCommissionSacrificePercentage;
      state.initialIndemnityPercentage = initialIndemnityPercentage;
    },
    [postApplicationCommission.rejected]: (state, action) => {
      state.error = action.error.message;
    },
    [postApplicationQuoteDate.pending]: (state) => {
      state.applicantStatus.quoteDate.loading = true;
      state.applicantStatus.quoteDate.error = false;
    },
    [postApplicationQuoteDate.fulfilled]: (state, action) => {
      const {
        arg: { quoteValidUntilDate },
      } = action.meta;
      state.validUntil = quoteValidUntilDate;
      state.applicantStatus.quoteDate.loading = false;
      state.applicantStatus.quoteDate.error = false;
    },
    [postApplicationQuoteDate.rejected]: (state, action) => {
      state.applicantStatus.quoteDate.error =
        action.payload?.errors ?? 'Unknown error';
      state.applicantStatus.quoteDate.loading = false;
    },
    [postApplicationMortgageRelated.fulfilled]: (state, action) => {
      state.isMortgageRelated = action.payload.data.isMortgageRelated;
    },
    [postApplicationMortgageRelated.rejected]: (state, action) => {
      state.error = action.error.message;
    },
    [postApplicationIsAdvisedSale.fulfilled]: (state, action) => {
      state.isAdvisedSale = action.payload.data.isAdvisedSale;
    },
    [postApplicationIsAdvisedSale.rejected]: (state, action) => {
      state.error = action.error.message;
    },
    [postApplicationPolicyStartDate.fulfilled]: (state, action) => {
      state.policyStartDate = action.payload.data.policyStartDate;
      state.policyStartDateOption = action.payload.data.policyStartDateOption;
    },
    [postApplicationPolicyStartDate.rejected]: (state, action) => {
      state.error = action.error.message;
    },
    [deleteApplicantById.pending]: (state) => {
      state.loading = true;
    },
    [deleteApplicantById.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants.splice(index, 1);
      if (index === 0) {
        state.applicantStatus.applicants[0] =
          state.applicantStatus.applicants[1];
      }
      state.applicantStatus.applicants[1] = status;
      state.applicants[0].sameAddressApplicantId = null;
      state.loading = false;
    },
    [deleteApplicantById.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [postActivateApplication.pending]: (state) => {
      state.active = false;
      state.applicantStatus.activate.loading = true;
    },
    [postActivateApplication.fulfilled]: (state) => {
      state.active = true;
      state.applicantStatus.activate.loading = false;
      state.applicantStatus.activate.error = false;
    },
    [postActivateApplication.rejected]: (state, action) => {
      state.active = false;
      state.applicantStatus.activate.loading = false;
      state.applicantStatus.activate.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [getApplicationStatus.pending]: (state) => {
      state.applicantStatus.status.loading = true;
    },
    [getApplicationStatus.fulfilled]: (state, action) => {
      if (action.payload.data) {
        const {
          application: { status, validUntil, isQuoteEditable },
          pageStatuses,
        } = action.payload.data;
        const isPortalQuote = current(state).isPortalQuote;
        state.applicationStatus = status;
        state.isQuoteEditable = isQuoteEditable;
        state.validUntil = validUntil;
        state.sections = current(state.sections).map((section) => ({
          ...section,
          ...pageStatuses
            .map((pageStatus) => ({
              disabled: isSectionDisabled(
                pageStatus.name,
                pageStatuses,
                status,
                isQuoteEditable,
                isPortalQuote
              ),
              isComplete: pageStatus.isComplete,
              id: pageStatus.name,
            }))
            .find((pageStatus) => pageStatus.id === section.id),
        }));
        state.applicantStatus.status.loading = false;
        state.tabsComplete =
          pageStatuses
            .filter((pageStatus) => pageStatus.name !== app_section_ids.SETUP)
            .filter((pageStatus) => pageStatus.isComplete !== true).length ===
          0;
      }
    },
    [getApplicationStatus.rejected]: (state, action) => {
      state.error = action.error.message;
      state.applicantStatus.status.loading = false;
    },
    [getApplicationPageStatus.pending]: (state) => {
      state.applicantStatus.status.loading = true;
    },
    [getApplicationPageStatus.fulfilled]: (state, action) => {
      if (action.payload.data) {
        const {
          application: { isQuoteEditable },
          pageStatuses,
        } = action.payload.data;
        const isPortalQuote = current(state).isPortalQuote;
        const applicationStatus = current(state).status;
        state.sections = current(state.sections).map((section) => ({
          ...section,
          ...pageStatuses
            .map((pageStatus) => ({
              disabled: isSectionDisabled(
                pageStatus.name,
                pageStatuses,
                applicationStatus,
                isQuoteEditable,
                isPortalQuote
              ),
              isComplete: pageStatus.isComplete,
              id: pageStatus.name,
            }))
            .find((pageStatus) => pageStatus.id === section.id),
        }));
        state.applicantStatus.status.loading = false;
        state.tabsComplete =
          pageStatuses
            .filter((pageStatus) => pageStatus.name !== app_section_ids.SETUP)
            .filter((pageStatus) => pageStatus.isComplete !== true).length ===
          0;
      }
    },
    [getApplicationPageStatus.rejected]: (state, action) => {
      state.error = action.error.message;
      state.applicantStatus.status.loading = false;
    },
    [postApplicationChild.pending]: (state) => {
      state.applicantStatus.children.loading = true;
    },
    [postApplicationChild.fulfilled]: (state, action) => {
      state.children.push(action.payload.data);
      state.hasUnbornChildren = false;
      state.applicantStatus.children.loading = false;
    },
    [postApplicationChild.rejected]: (state, action) => {
      state.applicantStatus.children.loading = false;
      state.applicantStatus.children.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [putApplicationChild.pending]: (state) => {
      state.applicantStatus.children.loading = true;
    },
    [putApplicationChild.fulfilled]: (state, action) => {
      const index = current(state.children).findIndex(
        (child) => child.id === action.payload?.data?.id
      );
      state.children[index] = action.payload.data;
      state.applicantStatus.children.loading = false;
    },
    [putApplicationChild.rejected]: (state, action) => {
      state.applicantStatus.children.loading = false;
      state.applicantStatus.children.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [deleteApplicationChild.pending]: (state) => {
      state.applicantStatus.children.loading = true;
    },
    [deleteApplicationChild.fulfilled]: (state, action) => {
      try {
        const childIndex = state.children.findIndex(
          (child) => child.id === action.meta.arg.id
        );
        if (childIndex === -1) {
          throw new Error('Child not found');
        }
        state.children.splice(childIndex, 1);
      } catch (error) {
        state.applicantStatus.children.error = error;
      }
      state.applicantStatus.children.loading = false;
    },
    [deleteApplicationChild.rejected]: (state, action) => {
      state.applicantStatus.children.loading = false;
      state.applicantStatus.children.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [getBankDetails.pending]: (state) => {
      state.applicantStatus.bankDetails.loading = true;
    },
    [getBankDetails.fulfilled]: (state, action) => {
      state.bankDetails = action.payload.data;
      state.applicantStatus.bankDetails.loading = false;
    },
    [getBankDetails.rejected]: (state, action) => {
      state.applicantStatus.bankDetails.loading = false;
      state.applicantStatus.bankDetails.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postBankDetails.pending]: (state) => {
      state.applicantStatus.bankDetails.loading = true;
    },
    [postBankDetails.fulfilled]: (state, action) => {
      state.bankDetails = action.payload.data.bankDetails;
      state.applicantStatus.bankDetails.loading = false;
    },
    [postBankDetails.rejected]: (state, action) => {
      state.applicantStatus.bankDetails.loading = false;
      state.applicantStatus.bankDetails.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postValidateBankDetails.pending]: (state) => {
      state.applicantStatus.bankDetails.validating = true;
    },
    [postValidateBankDetails.fulfilled]: (state) => {
      state.applicantStatus.bankDetails.validating = false;
    },
    [postValidateBankDetails.rejected]: (state, action) => {
      state.applicantStatus.bankDetails.validating = false;
      state.applicantStatus.bankDetails.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postResetBankValidation.fulfilled]: (state) => {
      state.bankDetails.bankCheckValidation = null;
      state.bankDetails.bankCheckResult = null;
      state.bankDetails.bankCheckStatus = null;
      state.bankDetails.isBankCheckConfirmed = null;
    },
    [postResetBankValidation.rejected]: (state, action) => {
      state.applicantStatus.bankDetails.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postOverrideBankValidation.pending]: (state) => {
      state.applicantStatus.bankDetails.validating = true;
    },
    [postOverrideBankValidation.fulfilled]: (state, action) => {
      state.applicantStatus.bankDetails.validating = false;
      state.bankDetails = action.payload.data;
    },
    [postOverrideBankValidation.rejected]: (state, action) => {
      state.applicantStatus.bankDetails.validating = false;
      state.applicantStatus.bankDetails.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postApplicantCoverIsPayoutPlannerUsed.pending]: (state) => {
      state.applicantStatus.payoutPlanner.loading = true;
    },
    [postApplicantCoverIsPayoutPlannerUsed.fulfilled]: (state, action) => {
      const { coverId } = action.meta.arg;
      try {
        const index = getApplicantIndexFromMeta(state, action);
        if (index === -1) throw new Error('Applicant not found');
        const coverIndex = state.applicants[index].covers.findIndex(
          (cover) => cover.id === coverId
        );
        if (coverIndex === -1) throw new Error('Cover not found');
        state.applicants[index].covers[coverIndex] = action.payload.data;
      } catch (error) {
        state.applicantStatus.payoutPlanner.error = error;
      }
      state.applicantStatus.payoutPlanner.loading = false;
    },
    [postApplicantCoverIsPayoutPlannerUsed.rejected]: (state, action) => {
      state.applicantStatus.payoutPlanner.loading = false;
      state.applicantStatus.payoutPlanner.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postApplicationCoverBeneficiary.pending]: (state) => {
      state.applicantStatus.beneficiaries.loading = true;
    },
    [postApplicationCoverBeneficiary.fulfilled]: (state, action) => {
      state.candidateBeneficiaries.push(action.payload.data);
      state.applicantStatus.beneficiaries.loading = false;
    },
    [postApplicationCoverBeneficiary.rejected]: (state, action) => {
      state.applicantStatus.beneficiaries.loading = false;
      state.applicantStatus.beneficiaries.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [putApplicationCoverBeneficiary.pending]: (state) => {
      state.applicantStatus.beneficiaries.loading = true;
    },
    [putApplicationCoverBeneficiary.fulfilled]: (state, action) => {
      const index = current(state.candidateBeneficiaries).findIndex(
        (beneficiary) => beneficiary.id === action.payload.data.id
      );
      state.candidateBeneficiaries[index] = action.payload.data;
      state.applicantStatus.beneficiaries.loading = false;
    },
    [putApplicationCoverBeneficiary.rejected]: (state, action) => {
      state.applicantStatus.beneficiaries.loading = false;
      state.applicantStatus.beneficiaries.error =
        action.payload?.errors ?? 'Unknown error';
    },

    [postApplicationCoverBeneficiaryAllocation.pending]: (state) => {
      state.applicantStatus.beneficiaries.loading = true;
    },
    [postApplicationCoverBeneficiaryAllocation.fulfilled]: (state, action) => {
      const { coverId } = action.meta.arg;
      try {
        const index = getApplicantIndexFromMeta(state, action);
        if (index === -1) throw new Error('Applicant not found');
        const coverIndex = state.applicants[index].covers.findIndex(
          (cover) => cover.id === coverId
        );
        if (coverIndex === -1) throw new Error('Cover not found');
        state.applicants[index].covers[coverIndex].benefitAllocations.push(
          action.payload.data
        );
      } catch (error) {
        state.applicantStatus.beneficiaries.error = error;
      }
      state.applicantStatus.beneficiaries.loading = false;
    },
    [postApplicationCoverBeneficiaryAllocation.rejected]: (state, action) => {
      state.applicantStatus.beneficiaries.loading = false;
      state.applicantStatus.beneficiaries.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [putApplicationCoverBeneficiaryAllocation.pending]: (state) => {
      state.applicantStatus.beneficiaries.loading = true;
    },
    [putApplicationCoverBeneficiaryAllocation.fulfilled]: (state, action) => {
      const { coverId, beneficiaryAllocationId } = action.meta.arg;
      try {
        const index = getApplicantIndexFromMeta(state, action);
        if (index === -1) throw new Error('Applicant not found');
        const coverIndex = state.applicants[index].covers.findIndex(
          (cover) => cover.id === coverId
        );
        if (coverIndex === -1) throw new Error('Cover not found');
        const beneficiaryIndex = state.applicants[index].covers[
          coverIndex
        ].benefitAllocations.findIndex(
          (benefitAllocation) =>
            benefitAllocation.id === beneficiaryAllocationId
        );
        if (beneficiaryIndex === -1) throw new Error('Beneficiary not found');
        state.applicants[index].covers[coverIndex].benefitAllocations[
          beneficiaryIndex
        ] = action.payload.data;
      } catch (error) {
        state.applicantStatus.beneficiaries.error = error;
      }
      state.applicantStatus.beneficiaries.loading = false;
    },
    [putApplicationCoverBeneficiaryAllocation.rejected]: (state, action) => {
      state.applicantStatus.beneficiaries.loading = false;
      state.applicantStatus.beneficiaries.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [deleteApplicationCoverBeneficiaryAllocation.pending]: (state) => {
      state.applicantStatus.beneficiaries.loading = true;
    },
    [deleteApplicationCoverBeneficiaryAllocation.fulfilled]: (
      state,
      action
    ) => {
      const { coverId, beneficiaryAllocationId } = action.meta.arg;
      try {
        const index = getApplicantIndexFromMeta(state, action);
        if (index === -1) throw new Error('Applicant not found');
        const coverIndex = state.applicants[index].covers.findIndex(
          (cover) => cover.id === coverId
        );
        if (coverIndex === -1) throw new Error('Cover not found');
        const beneficiaryIndex = state.applicants[index].covers[
          coverIndex
        ].benefitAllocations.findIndex(
          (benefitAllocation) =>
            benefitAllocation.id === beneficiaryAllocationId
        );
        if (beneficiaryIndex === -1) throw new Error('Beneficiary not found');
        state.applicants[index].covers[coverIndex].benefitAllocations.splice(
          beneficiaryIndex,
          1
        );
      } catch (error) {
        state.applicantStatus.beneficiaries.error = error;
      }
      state.applicantStatus.beneficiaries.loading = false;
    },
    [deleteApplicationCoverBeneficiaryAllocation.rejected]: (state, action) => {
      state.applicantStatus.beneficiaries.loading = false;
      state.applicantStatus.beneficiaries.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postApplicationDecision.pending]: (state) => {
      state.applicantStatus.decision.loading = true;
    },
    [postApplicationDecision.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      if (index !== -1) {
        state.applicants[index].riskDecisions =
          action.payload.data.riskDecisions;
      }
      state.applicantStatus.decision.loading = false;
    },
    [postApplicationDecision.rejected]: (state, action) => {
      state.applicantStatus.decision.loading = false;
      state.applicantStatus.decision.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postApplicationQiDecision.pending]: (state) => {
      state.applicantStatus.decision.loading = true;
    },
    [postApplicationQiDecision.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      if (index !== -1) {
        state.applicants[index].riskDecisions =
          action.payload.data.riskDecisions;
      }
      state.applicantStatus.decision.loading = false;
    },
    [postApplicationQiDecision.rejected]: (state, action) => {
      state.applicantStatus.decision.loading = false;
      state.applicantStatus.decision.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [getApplicationUnderwritingUpdates.pending]: (state) => {
      state.applicantStatus.underwritingUpdates.loading = true;
    },
    [getApplicationUnderwritingUpdates.fulfilled]: (state, action) => {
      state.applicantsUnderwritingUpdates = action.payload.data.applicants;
      state.applicantStatus.underwritingUpdates.loading = false;
    },
    [getApplicationUnderwritingUpdates.rejected]: (state, action) => {
      state.applicantStatus.underwritingUpdates.loading = false;
      state.applicantStatus.underwritingUpdates.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postHasUnbornChildren.pending]: (state) => {
      state.applicantStatus.hasUnbornChildren.loading = true;
    },
    [postHasUnbornChildren.fulfilled]: (state, action) => {
      const {
        arg: { hasUnbornChildren },
      } = action.meta;
      state.hasUnbornChildren = hasUnbornChildren;
      state.applicantStatus.hasUnbornChildren.loading = false;
    },
    [postHasUnbornChildren.rejected]: (state, action) => {
      state.applicantStatus.hasUnbornChildren.loading = false;
      state.applicantStatus.hasUnbornChildren.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postPortalApply.pending]: (state) => {
      state.applicantStatus.portal.loading = true;
    },
    [postPortalApply.fulfilled]: (state, action) => {
      state.applicantStatus.portal.applicationId =
        action.payload.data.applicationId;
      state.applicantStatus.portal.loading = false;
    },
    [postPortalApply.rejected]: (state, action) => {
      state.applicantStatus.portal.loading = false;
      state.applicantStatus.portal.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [postUnlockQuoteEdit.pending]: (state) => {
      state.applicantStatus.requote.loading = true;
    },
    [postUnlockQuoteEdit.fulfilled]: (state) => {
      state.applicantStatus.requote.loading = false;
    },
    [postUnlockQuoteEdit.rejected]: (state, action) => {
      state.applicantStatus.requote.loading = false;
      state.applicantStatus.requote.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [getApplicationUnenriched.pending]: (state) => {
      state.applicantStatus.unenriched.loading = true;
    },
    [getApplicationUnenriched.fulfilled]: (state, action) => {
      state.applicantStatus.unenriched.loading = false;
      state.active = true;
      state.applicationUnenriched = action.payload.data;
    },
    [getApplicationUnenriched.rejected]: (state, action) => {
      state.applicantStatus.unenriched.loading = false;
      state.applicationUnenriched = null;
      state.applicantStatus.unenriched.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [getIsTermExtensible.pending]: (state) => {
      state.applicantStatus.isTermExtensible.loading = true;
    },
    [getIsTermExtensible.fulfilled]: (state, action) => {
      state.applicantStatus.isTermExtensible.loading = false;
      state.isTermExtensible = action.payload.data.showExtend;
    },
    [getIsTermExtensible.rejected]: (state, action) => {
      state.applicantStatus.isTermExtensible.loading = false;
      state.applicationisTermExtensible = null;
      state.applicantStatus.isTermExtensible.error =
        action.payload?.errors ?? 'Unknown error';
    },
    [triggerDeclarationOfHealthQuestions.pending]: (state) => {
      state.applicantStatus.declarationhHealthQuestions.loading = true;
    },
    [triggerDeclarationOfHealthQuestions.fulfilled]: (state, action) => {
      state.applicantStatus.declarationhHealthQuestions.loading = false;
      state.termExtensible = action.payload.data;
    },
    [triggerDeclarationOfHealthQuestions.rejected]: (state, action) => {
      state.applicantStatus.declarationhHealthQuestions.loading = false;
      state.termExtensible = null;
      state.applicantStatus.declarationhHealthQuestions.error =
        action.payload?.errors;
    },
    [completeTermExtension.pending]: (state) => {
      state.applicantStatus.completeTermExtension.loading = true;
      state.applicantStatus.completeTermExtension.error = null;
      state.applicantStatus.completeTermExtension.status = null;
    },
    [completeTermExtension.fulfilled]: (state, action) => {
      state.applicantStatus.completeTermExtension.loading = false;
      state.applicantStatus.completeTermExtension.status =
        action.payload.data.declarationOfHealthStatus;
    },
    [completeTermExtension.rejected]: (state, action) => {
      state.applicantStatus.completeTermExtension.loading = false;
      state.applicantStatus.completeTermExtension.error = action.error;
      state.applicantStatus.completeTermExtension.status = 'Error';
    },
    [postAmraConsentGranted.pending]: (state) => {
      state.applicantStatus.amraConsentGranted.error = null;
      state.applicantStatus.amraConsentGranted.loading = true;
    },
    [postAmraConsentGranted.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].isAmraConsentGranted =
        action.payload.data.isAmraConsentGranted;
      state.applicantStatus.amraConsentGranted.error = null;
      state.applicantStatus.amraConsentGranted.loading = false;
    },
    [completeTermExtension.rejected]: (state, action) => {
      state.applicantStatus.amraConsentGranted.loading = false;
      state.applicantStatus.amraConsentGranted.error = action.error;
    },
    [postViewMedicalReportsRequested.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].isViewMedicalReportsRequested =
        action.payload.data.isViewMedicalReportsRequested;
    },
    [patchApplicantExaminerDetails.fulfilled]: (state, action) => {
      const index = getApplicantIndexFromMeta(state, action);
      state.applicants[index].examiner = action.payload.data;
    },
  },
});

export const {
  reset,
  setApplicant,
  setApplicantData,
  addApplicantCover,
  removeApplicantCover,
  setApplicant1EnquiryId,
  setApplicant2EnquiryId,
  setMortgageRelated,
  updateApplicantCover,
  updateApplicantDecisionCover,
  setApplicantSameAddressId,
  setCustomerPrivacyPolicySubmitted,
  setApplicantContactDetails,
  setApplicantPristine,
  removeApplicant,
  setBankDetailsInvalid,
  setTermExtensibleAnswer,
  resetTermExtensible,
  setExclusive,
  deleteExclusive,
  updateApplicantExaminer,
} = applicationSlice.actions;

export default applicationSlice;
